import React from "react";
import InputField from "../../SharedComponent/Fields/InputField";
import { useTranslation } from "react-i18next";
import SelectField from "../../SharedComponent/Fields/SelectField";
import TextAreaFielD from "../../SharedComponent/Fields/TextareaField";
import { ImagePlus, Upload, X } from "lucide-react";
import { getAttachmentLargeUrl, getAttachmentThumbUrl, getAttachmentDocumentUrl } from "../../utils/attachmentImages";

const WorkPlatforms = ({
  formatAttachment,
  handleDeleteAttachment,
  assetAttachments,
  newAsset,
  handleChange,
  warehouses,
  selectedWarehouse,
  handleWarehouseChange,
  warehouseLocationId,
  selectedFiles,
  handleRemoveFile,
  handleFileChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Basics Section */}
      <div className="bg-white p-4 shadow-md">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.basics")}
        </h2>

        {/* Row 1: Classification, Manufacturer, Type, Serial No */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <SelectField
            name="workplatform_toc"
            value={newAsset.workplatform_toc}
            onChange={handleChange}
            label={t("assets.fields.classification")}
            explanation={t("assets.fields.explanation.classification")}
            placeholder={t("assets.fields.selectClassification")}
            required

            menus={[
              {
                value: "",
                option: t("workPlatform.classificationTypes.select"),
              },
              { value: 1, option: t("workPlatform.classificationTypes.other") },
              {
                value: 2,
                option: t("workPlatform.classificationTypes.trailed"),
              },
              {
                value: 3,
                option: t("workPlatform.classificationTypes.telescope"),
              },
              {
                value: 4,
                option: t("workPlatform.classificationTypes.vertical"),
              },
              {
                value: 5,
                option: t("workPlatform.classificationTypes.articulated"),
              },
              {
                value: 6,
                option: t("workPlatform.classificationTypes.scissors"),
              },
              {
                value: 7,
                option: t("workPlatform.classificationTypes.truckMounted"),
              },
              {
                value: 8,
                option: t("workPlatform.classificationTypes.special"),
              },
              {
                value: 9,
                option: t("workPlatform.classificationTypes.spider"),
              },
            ]}
            selectClassName={
    !newAsset.workplatform_toc
      ? "text-gray-400 font-light"
      : "text-gray-900 font-normal"
  }
          />
          <InputField
            name="manufacturer"
            value={newAsset.manufacturer}
            required

            onChange={handleChange}
            label={t("assets.fields.manufacturer")}
            explanation={t("assets.fields.explanation.manufacturer")}
            placeholder={t("createAsset.placeholder.enterManufacturer")}
            maxLength={100}
          />

          <InputField
            name="type"
            value={newAsset.type}
            onChange={handleChange}
            label={t("assets.fields.type")}
            placeholder={t("createAsset.placeholder.enterType")}
            explanation={t("assets.fields.explanation.type")}
            maxLength={30}
          />

          <InputField
            name="serial_no"
            value={newAsset.serial_no}
            onChange={handleChange}
            placeholder={t("createAsset.placeholder.enterSerialNumber")}
            explanation={t("assets.fields.serialNumber")}
            label={t("assets.fields.machineSerialNo")}
            maxLength={100}
          />
        </div>

        {/* Row 2: Internal No, Status, Engine Type, Basket Load */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
          <InputField
            name="workplatform_internalno"
            value={newAsset.workplatform_internalno}
            onChange={handleChange}
            maxLength={100}
            label={t("createAsset.placeholders.internNoLabel")}
            explanation={t("createAsset.placeholders.internNoExplanation")}
            placeholder={t("createAsset.placeholders.internNoPlaceholder")}
          />

          <SelectField
            name="status"
            value={newAsset.status}
            onChange={handleChange}
            label={t("assets.fields.status")}
            placeholder={t("createAsset.placeholder.enterStatus")}
            explanation={t("assets.fields.explanation.status")}
            menus={[
              { value: "", option: t("assets.fields.selectStatus") },
              { value: "New", option: t("assets.options.new") },
              { value: "Used", option: t("assets.options.used") },
              { value: "Demo", option: t("assets.options.demo") },
            ]}
          />

          <SelectField
            name="workplatform_enginetype"
            value={newAsset.workplatform_enginetype}
            onChange={handleChange}
            label={t("createAsset.placeholders.engineTypeLabel")}
            explanation={t("createAsset.placeholders.engineTypeExplanation")}
            placeholder={t("createAsset.placeholders.engineTypePlaceholder")}
            required
            menus={[
              { value: "", option: t("workPlatform.placeholders.engineType") },
              { value: 1, option: t("assets.options.diesel") },
              { value: 2, option: t("assets.options.electric") },
              { value: 3, option: t("assets.options.lpg") },
              { value: 4, option: t("assets.options.gasoline-gas") },
              { value: 5, option: t("assets.options.hybrid") },
              { value: 6, option: t("assets.options.fuel-cell") },
              { value: 7, option: t("assets.options.lithium-ion") },
              { value: 8, option: t("assets.options.hand") },
            ]}
          />

          <InputField
            name="workplatform_basketload"
            type="number"
            value={newAsset.workplatform_basketload}
            onChange={handleChange}
            label={t("workPlatform.fields.basketLoad")}
            placeholder={t("workPlatform.placeholders.basketLoad")}
            explanation={t("workPlatform.explanations.basketLoad")}
          />
        </div>

        {/* Row 3: Battery, Capacity, Height, Length */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
          <InputField
            name="workplatform_battery"
            value={newAsset.workplatform_battery}
            onChange={handleChange}
            label={t("workPlatform.fields.battery")}
            placeholder={t("workPlatform.placeholders.battery")}
            explanation={t("workPlatform.explanations.battery")}
            maxLength={50}
          />

          <InputField
            name="capacity"
            type="number"
            value={newAsset.capacity}
            onChange={handleChange}
            label={t("workPlatform.fields.capacity")}
            placeholder={t("workPlatform.placeholders.capacity")}
            explanation={t("workPlatform.explanations.capacity")}
          />

          <InputField
            name="workplatform_height"
            type="number"
            value={newAsset.workplatform_height}
            onChange={handleChange}
            label={t("workPlatform.fields.height")}
            placeholder={t("workPlatform.placeholders.height")}
            explanation={t("workPlatform.explanations.height")}
          />

          <InputField
            name="workplatform_length"
            type="number"
            value={newAsset.workplatform_length}
            onChange={handleChange}
            label={t("workPlatform.fields.length")}
            placeholder={t("workPlatform.placeholders.length")}
            explanation={t("workPlatform.explanations.length")}
          />
        </div>

        {/* Row 4: Weight, Width, Working Height, Condition */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
          <InputField
            name="workplatform_weight"
            type="number"
            value={newAsset.workplatform_weight}
            onChange={handleChange}
            label={t("workPlatform.fields.weight")}
            placeholder={t("workPlatform.placeholders.weight")}
            explanation={t("workPlatform.explanations.weight")}
          />

          <InputField
            name="workplatform_width"
            type="number"
            value={newAsset.workplatform_width}
            onChange={handleChange}
            label={t("workPlatform.fields.width")}
            placeholder={t("workPlatform.placeholders.width")}
            explanation={t("workPlatform.explanations.width")}
          />

          <InputField
            name="workplatform_workheight"
            type="number"
            value={newAsset.workplatform_workheight}
            onChange={handleChange}
            label={t("workPlatform.fields.workingHeight")}
            placeholder={t("workPlatform.placeholders.workingHeight")}
            explanation={t("workPlatform.explanations.workingHeight")}
          />

          {/* <InputField
            name="workplatform_available"
            type="date"
            value={newAsset.workplatform_available}
            onChange={handleChange}
            label={t("workPlatform.fields.availableFrom")}
            explanation={t("workPlatform.explanations.availableFrom")}
          /> */}
          <InputField
            name="year_of_manufacture"
            type="date"
            value={newAsset.year_of_manufacture || ""}
            onChange={handleChange}
            label={t("workPlatform.fields.availableFrom")}
            explanation={t("workPlatform.explanations.availableFrom")}
          />
          
        </div>

        {/* Row 5: Available From */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {/* <div>
            <InputField
              name="year_of_manufacture"
              value={newAsset.year_of_manufacture || ""}
              onChange={handleChange}
              label={t("assets.fields.yearOfManufacture")}
              explanation={t("assets.fields.explanation.yearOfManufacture")}
              placeholder={t("createAsset.placeholder.enterYearOfManufacture")}
              type="month"
              maxLength={100}
            />
          </div> */}
          <SelectField
            name="technical_condition"
            value={newAsset.technical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.technicalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.technicalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.technicalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.technicalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.technicalCondition.very_good"),
              },
              {
                value: 3,
                option: t("assets.technicalCondition.good"),
              },
              {
                value: 4,
                option: t("assets.technicalCondition.used"),
              },
              {
                value: 5,
                option: t("assets.technicalCondition.bad"),
              },
              {
                value: 6,
                option: t("assets.technicalCondition.very_bad"),
              },
              {
                value: 7,
                option: t("assets.technicalCondition.new"),
              },
            ]}
          />
          <SelectField
            name="optical_condition"
            value={newAsset.optical_condition}
            onChange={handleChange}
            label={t("createAsset.placeholders.opticalConditionLabel")}
            explanation={t(
              "createAsset.placeholders.opticalConditionExplanation"
            )}
            placeholder={t(
              "createAsset.placeholders.opticalConditionPlaceholder"
            )}
            menus={[
              {
                value: "",
                option: t(
                  "createAsset.placeholders.opticalConditionPlaceholder"
                ),
              },
              {
                value: 2,
                option: t("assets.opticalCondition.very_good"),
              },
              { value: 3, option: t("assets.opticalCondition.good") },
              { value: 4, option: t("assets.opticalCondition.used") },
              { value: 5, option: t("assets.opticalCondition.bad") },
              { value: 6, option: t("assets.opticalCondition.very_bad") },
              { value: 7, option: t("assets.opticalCondition.new") },
            ]}
          />
        </div>

        {/* Row 6: Warehouse + Location */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mt-2">
          {Array.isArray(warehouses) && warehouses?.length > 0 && (
            <SelectField
              name="warehouse"
              value={selectedWarehouse?.id || warehouseLocationId || ""}
              onChange={handleWarehouseChange}
              label="Warehouse"
              menus={[
                { value: "", option: "Select Warehouse" },
                ...warehouses.map((w) => ({ value: w.id, option: w.name })),
              ]}
            />
          )}

          <div>
            <InputField
              required
              name="dealer_street"
              value={newAsset.dealer_street}
              onChange={handleChange}
              maxLength={20}
              label={t("createAsset.placeholders.street")}
              placeholder={t("createAsset.placeholders.streetPlaceholder")}
              explanation={t("createAsset.placeholders.streetExplanation")}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_city"
              value={newAsset.dealer_city}
              onChange={handleChange}
              label={t("createAsset.placeholders.city")}
              explanation={t("createAsset.placeholders.cityExplanation")}
              placeholder={t("createAsset.placeholders.cityPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_country"
              value={newAsset.dealer_country}
              onChange={handleChange}
              label={t("createAsset.placeholders.country")}
              explanation={t("createAsset.placeholders.countryExplanation")}
              placeholder={t("createAsset.placeholders.countryPlaceholder")}
              maxLength={50}
            />
          </div>

          <div>
            <InputField
              required
              name="dealer_zip"
              value={newAsset.dealer_zip}
              onChange={handleChange}
              type="number"
              label={t("createAsset.placeholders.zipCode")}
              explanation={t("createAsset.placeholders.zipCodePlaceholder")}
              placeholder={t("createAsset.placeholders.zipCodeExplanation")}
              maxLength={7}
            />
          </div>
        </div>

        {/* Row 7: Additional Description */}
        <div className="mt-2">
          <TextAreaFielD
            name="description"
            value={newAsset.description}
            onChange={handleChange}
            label={t("createAsset.placeholders.additionalDescription")}
            placeholder={t(
              "createAsset.placeholders.additionalDescriptionPlaceholder"
            )}
          />
        </div>
      </div>

      {/* Media Upload Section */}
      <div className="bg-white p-4 shadow-md mt-4">
        <h2 className="text-xl font-medium mb-3 text-gray-800">
          {t("assetPageHeadings.media")}
        </h2>

        {/* Selected Files */}
        {selectedFiles?.length > 0 && (
          <div className="space-y-2 mt-2">
            <h6>Selected Media</h6>
            <ul className="space-y-1 flex flex-col gap-3">
              {Array.from(selectedFiles).map((file, index) => (
                <li key={index} className="flex items-center gap-4">
                  {file.type.startsWith("image/") && (
                    <img
                      className="h-10 w-10"
                      alt={`workplatform ${index}`}
                      src={URL.createObjectURL(file)}
                    />
                  )}
                  <a
                    href={URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm font-semibold underline ml-2"
                  >
                    {file.name}
                  </a>
                  <div
                    onClick={() => handleRemoveFile(index)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Uploaded Media */}
        {assetAttachments?.length > 0 && (
          <div className="flex flex-col gap-2 mt-4">
            <h6>Uploaded Media</h6>
            <div className="flex flex-col gap-3">
              {assetAttachments.map((attachment, index) => (
                <div key={index} className="flex items-center gap-2">
                  {attachment?.image ? (
                    <>
                      <img
                        className="h-10 w-10"
                        src={getAttachmentThumbUrl(attachment)}
                        alt="workplatform"
                      />
                      <a
                        href={getAttachmentLargeUrl(attachment)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 text-sm font-semibold underline ml-2"
                      >
                        {formatAttachment(getAttachmentLargeUrl(attachment))}
                      </a>
                    </>
                  ) : (
                    <a
                      href={getAttachmentDocumentUrl(attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm font-semibold underline ml-2"
                    >
                      {formatAttachment(getAttachmentDocumentUrl(attachment))}
                    </a>
                  )}
                  <div
                    onClick={() => handleDeleteAttachment(attachment?.id)}
                    className="cursor-pointer"
                  >
                    <X className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Upload */}
        <div className="my-4">
          <div className="relative">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              accept="image/*, .xlsx, .xls, .doc, .docx, .pdf"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              id="workplatform-upload"
            />
            <label
              htmlFor="workplatform-upload"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-gray-400"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <ImagePlus className="w-6 h-6 text-gray-400" />
                </div>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">
                    {t("assetPageHeadings.clickToUpload")}
                  </span>
                </p>
                <p className="text-xs text-gray-400">
                  {t("assetPageHeadings.fileSupported")}
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkPlatforms;
